import { Company } from "@/hooks/companies/useCompanyDetail";
import { CompanyOverviewCard } from "@/components/companies/CompanyOverviewCard";
import { CompanyLogoEdit } from "@/components/companies/CompanyLogoEdit";
import { CompanyEditTabs } from "@/components/companies/CompanyEditTabs";
import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { EntityEditForm } from "@/components/common/EntityEditForm";
import { useEntityEditForm } from "@/hooks/common/useEntityEditForm";
import { StickyEditBanner } from "@/components/ui/StickyEditBanner";
import { fetchCustomFields, CustomFieldsJSON } from "@/integrations/supabase/customFieldsApi";
import { useAuth } from "@/providers/AuthProvider";

interface CompanyProfileProps {
  company: Company;
  isEditing: boolean;
  onChange: (company: Company) => void;
  onSave: () => void;
  onCancel: () => void;
  refreshCompany?: () => void;
}

export const CompanyProfile = ({
  company,
  isEditing,
  onChange,
  onSave,
  onCancel,
  refreshCompany
}: CompanyProfileProps) => {
  const { t } = useTranslation();
  const bannerRef = useRef<HTMLDivElement>(null);
  const [isSticky, setIsSticky] = useState(false);
  const { currentOrganization } = useAuth();
  const [customFields, setCustomFields] = useState<any[]>([]);
  const [customFieldsLoading, setCustomFieldsLoading] = useState(false);
  const [customFieldsError, setCustomFieldsError] = useState<string | null>(null);

  // Use the reusable EntityEditForm hook
  const editFormProps = useEntityEditForm({
    entityType: 'company',
    entity: company,
    onChange: (field, value) => onChange && onChange({ ...company, [field]: value })
  });

  const handleLogoChange = (logoUrl: string | undefined) => {
    onChange && onChange({ ...company, logo_url: logoUrl || null });
  };

  const handleClearLogo = () => {
    onChange && onChange({ ...company, logo_url: null });
  };

  useEffect(() => {
    const handleScroll = () => {
      if (!bannerRef.current) return;
      const rect = bannerRef.current.getBoundingClientRect();
      setIsSticky(rect.top <= 0);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Fetch custom fields configuration
  useEffect(() => {
    if (!currentOrganization?.id) return;
    setCustomFieldsLoading(true);
    setCustomFieldsError(null);
    fetchCustomFields(currentOrganization.id)
      .then((data: CustomFieldsJSON) => {
        setCustomFields(data.Companies || []);
      })
      .catch(() => {
        setCustomFieldsError("Failed to load custom fields");
      })
      .finally(() => setCustomFieldsLoading(false));
  }, [currentOrganization?.id]);

  return (
    <div className="flex flex-col gap-6">
      {isEditing && (
        <>
          <StickyEditBanner
            isSticky={isSticky}
            onSave={onSave}
            onCancel={onCancel}
          >
            {t('common.editMode', 'Edit mode')}
          </StickyEditBanner>
        </>
      )}
      
      <div className={`w-full bg-white dark:bg-gray-900 rounded-sm shadow p-6 relative ${isEditing ? 'ring-2 ring-blue-400 bg-blue-50 dark:bg-blue-950' : ''}`}>
        {isEditing ? (
          <div className="space-y-6">
            {/* Logo Section */}
            <CompanyLogoEdit 
              logoUrl={company.logo_url} 
              companyName={company.name} 
              vatNumber={company.vat_number}
              onLogoChange={handleLogoChange} 
            />
            
            {/* Company Edit Tabs */}
            <CompanyEditTabs
              company={company}
              isEditing={isEditing}
              onChange={(field, value) => onChange({ ...company, [field]: value })}
              customFieldsConfig={customFields}
            />
          </div>
        ) : (
          <CompanyOverviewCard company={company} customFieldsConfig={customFields} />
        )}
      </div>
    </div>
  );
}; 